import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/base/MovieCard";
import FilterBar from "@/components/feature/FilterBar";
import SectionHeader from "@/components/base/SectionHeader";
import usePageMeta from "@/hooks/usePageMeta";
import { allYearOptions, getHotMovies, regionOptions, searchMovies } from "@/utils/movie";

const typeOptions = ["全部", "电影", "电视剧"] as const;

const filterGroups = [
  { key: "type", label: "类型", options: typeOptions },
  { key: "region", label: "地区", options: regionOptions },
  { key: "year", label: "年份", options: allYearOptions },
];

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get("q") || "";

  const [input, setInput] = useState(q);
  const [filters, setFilters] = useState({ type: "全部", region: "全部", year: "全部" });

  useEffect(() => {
    setInput(q);
    setFilters({ type: "全部", region: "全部", year: "全部" });
  }, [q]);

  usePageMeta({
    title: q ? `${q} - 搜索结果 - 蓝莓视频` : "搜索 - 蓝莓视频高清影视资源",
    description: `在蓝莓视频搜索“${q || "影视作品"}”相关的电影、电视剧等高清影视资源。`,
    keywords: `蓝莓视频,影视搜索,${q || "在线视频"},高清影视资源`,
  });

  const results = useMemo(() => {
    if (!q.trim()) return [];
    return searchMovies(q).filter((m) => {
      if (filters.type !== "全部") {
        const label = m.type === "movie" ? "电影" : "电视剧";
        if (label !== filters.type) return false;
      }
      if (filters.region !== "全部" && m.region !== filters.region) return false;
      if (filters.year !== "全部" && String(m.year) !== filters.year) return false;
      return true;
    });
  }, [q, filters]);

  const hot = getHotMovies(6);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-16">
        <div className="px-4 md:px-8 py-8 md:py-10 bg-background-100 border-b border-background-200/70">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">搜索</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-5">
            影视搜索
          </h1>

          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500 text-base"></i>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影视名称、导演或演员"
                aria-label="搜索影视"
                className="w-full bg-background-50 border border-background-200 rounded-md pl-11 pr-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>

          {q && (
            <p className="mt-5 text-sm text-foreground-600">
              关键词：<span className="text-accent-500 font-medium">{q}</span> · 共找到{" "}
              <span className="text-accent-500 font-medium">{results.length}</span> 条结果
            </p>
          )}
        </div>

        <div className="px-4 md:px-8 py-8 md:py-10">
          {q ? (
            <>
              {results.length > 0 && (
                <div className="mb-6">
                  <FilterBar groups={filterGroups} values={filters} onChange={handleFilterChange} />
                </div>
              )}

              {results.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5">
                  {results.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-search-eye-line text-3xl"></i>
                  </span>
                  <h3 className="text-lg font-medium text-foreground-900 mb-2">
                    未找到与“{q}”相关的影视作品
                  </h3>
                  <p className="text-sm text-foreground-500 mb-8">
                    试试更换关键词，或看看下面的热门推荐
                  </p>
                  <div className="w-full">
                    <SectionHeader icon="ri-fire-line" title="热门影视推荐" moreTo="/category/hot" />
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-5">
                      {hot.map((movie) => (
                        <MovieCard key={movie.id} movie={movie} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="py-10">
              <SectionHeader icon="ri-fire-line" title="大家都在搜" subtitle="热门影视推荐" moreTo="/category/hot" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-5">
                {hot.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}